import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import OkCancelModal from '@/shared/components/OkCancelModal';
import intlShape from '@/shared/utils/intlShape';
import handleHttpError from '@/shared/services/HandleHttpError';
import FormattedListValue from '@/shared/components/FormattedListValue';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import translationHelper from './TranslationHelper';
import TranslateListItem from './TranslateListItem';

const messages = defineMessages({
  Translate: 'Translate',
});

class TranslateListItemModal extends PureComponent {
  state = {
    translations: null,
    saving: false,
    showValidationErrors: false,
  };

  loadTranslations = () => {
    const { list, listId, fallback, intl } = this.props;
    return translationHelper.retrieve(list, listId, fallback).then(translations => {
      this.setState({ translations, showValidationErrors: false });
    }, handleHttpError(intl));
  };

  componentDidMount() {
    const { show } = this.props;
    if (show) this.loadTranslations();
  }

  componentDidUpdate(oldProps) {
    const { list, listId, show } = this.props;
    const { translations } = this.state;
    if (list !== oldProps.list || listId !== oldProps.listId) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ translations: null }, () => {
        if (show) {
          this.loadTranslations();
        }
      });
    } else if (!oldProps.show && show && !translations) {
      this.loadTranslations();
    }
  }
  updateTranslations = translations => {
    this.setState({ translations });
  };

  saveTranslations = () => {
    const { translations } = this.state;
    const { intl, onClose, list, listId, defaultRequired } = this.props;
    if (defaultRequired && !translationHelper.validate(translations)) return Promise.reject();

    this.setState({ saving: true });
    const tenantLanguage = translations.find(x => x.isTenantLanguage);
    return translationHelper.save(translations).then(
      changes => {
        this.setState({ saving: false }, () => {
          onClose({
            list,
            listId,
            changes,
            fallback: tenantLanguage ? tenantLanguage.value : '',
          });
        });
      },
      error => {
        handleHttpError(intl)(error);
        this.setState({ saving: false });
      },
    );
  };
  closeModal = () => {
    const { onClose } = this.props;
    this.setState({ translations: null, saving: false, showValidationErrors: false }, () => {
      onClose(null);
    });
  };

  showValidationErrors = () => {
    this.setState({ showValidationErrors: true });
  };

  render() {
    const {
      list,
      listId,
      fallback,
      show,
      maxLength,
      textarea,
      title,
      defaultRequired,
    } = this.props;
    const { translations, saving, showValidationErrors } = this.state;

    const isValid = translationHelper.validate(translations, defaultRequired);

    return (
      <OkCancelModal
        title={
          <span>
            <FormattedMessage {...messages.Translate} />
            {fallback && (
              <>
                &nbsp;
                <FormattedListValue list={list} id={listId} fallback={fallback} />
              </>
            )}
          </span>
        }
        show={show}
        onClose={this.closeModal}
        onSave={this.saveTranslations}
        onHoverSave={this.showValidationErrors}
      >
        {!show || !translations ? (
          <LoadingAnimation />
        ) : (
          <div className="form-horizontal" role="form">
            <TranslateListItem
              translations={translations}
              onChange={this.updateTranslations}
              showValidation={showValidationErrors}
              disabled={saving}
              maxLength={maxLength}
              textarea={textarea}
              required={defaultRequired}
            />
          </div>
        )}
      </OkCancelModal>
    );
  }
}

TranslateListItemModal.propTypes = {
  intl: intlShape.isRequired,
  list: PropTypes.string.isRequired,
  listId: PropTypes.string.isRequired,
  fallback: PropTypes.string,
  maxLength: PropTypes.number,
  textarea: PropTypes.bool,
  show: PropTypes.bool,
  defaultRequired: PropTypes.bool,
};

TranslateListItemModal.defaultProps = {
  fallback: null,
  show: false,
  maxLength: undefined,
  textarea: false,
  defaultRequired: true,
};
export default injectIntl(TranslateListItemModal);
